/**
 * replay.ts — experience-replay buffer for Fitted Q-Iteration.
 *
 * FQI is BATCH: it collects transitions for a while, then refits Q on the
 * whole batch at once. The buffer stores (s, a, r, s', done) tuples in a
 * ring; old transitions fall off the back as new ones arrive.
 *
 * Storage layout — we keep FOUR flat typed-array pools:
 *   X      (B × N_FEATURES)         the action we took's features
 *   R      (B)                      the reward observed
 *   Xnext  (B × N_ACTIONS × N_FEATURES)   the FULL next-state candidate matrix
 *   Done   (B)                      1 if the episode ended on this transition
 *
 * We store the full next-state candidate matrix (not just the action taken)
 * because computing the bootstrap target r + γ·max_a' Q(s', a') needs Q for
 * ALL 32 candidates of s'. Storing them avoids recomputing features at
 * fit-time.
 *
 * Why a ring (not a sampled minibatch): linear FQI's closed-form solve is
 * exact on the whole buffer — there's no benefit to minibatching for a 12×12
 * system. The whole buffer is the dataset; older transitions age out.
 */

import { N_FEATURES, N_ACTIONS } from './features';

/** One transition's worth of memory. */
const X_STRIDE = N_FEATURES; // per transition
const XN_STRIDE = N_ACTIONS * N_FEATURES; // per transition

export class Replay {
  readonly capacity: number;
  readonly X: Float64Array;
  readonly R: Float64Array;
  readonly Xnext: Float64Array;
  readonly Done: Uint8Array;
  /** Number of valid transitions currently stored. */
  size = 0;
  /** Ring write cursor. */
  private cursor = 0;

  constructor(capacity: number) {
    this.capacity = capacity;
    this.X = new Float64Array(capacity * X_STRIDE);
    this.R = new Float64Array(capacity);
    this.Xnext = new Float64Array(capacity * XN_STRIDE);
    this.Done = new Uint8Array(capacity);
  }

  /**
   * Append one transition.
   *   `xTaken`    — N_FEATURES values, the action we took's feature row
   *   `reward`    — scalar reward observed
   *   `xNextAll`  — N_ACTIONS × N_FEATURES values, the full candidate matrix
   *                 of the resulting state (or the terminal state's matrix)
   *   `done`      — true if the episode ended on this step
   */
  add(xTaken: Float64Array, reward: number, xNextAll: Float64Array, done: boolean): void {
    const i = this.cursor;
    this.X.set(xTaken.subarray(0, X_STRIDE), i * X_STRIDE);
    this.R[i] = reward;
    this.Xnext.set(xNextAll.subarray(0, XN_STRIDE), i * XN_STRIDE);
    this.Done[i] = done ? 1 : 0;
    this.cursor = (this.cursor + 1) % this.capacity;
    if (this.size < this.capacity) this.size++;
  }

  /** Reset to empty (called at the start of a fresh training run). */
  clear(): void {
    this.size = 0;
    this.cursor = 0;
  }
}
